import { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../api/axios';

const C = { green: '#3cb74e', black: '#111111', white: '#ffffff', red: '#ef4444', amber: '#f59e0b', gray: '#64748b', border: '#e2e8f0' };

export default function VerificationChart() {
  const [data, setData]     = useState([]);
  const [period, setPeriod] = useState('weekly');
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchData(); }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/email/stats/chart?period=${period}`);
      setData(res.data.data.map(d => ({ ...d, date: d.date?.slice(0, 10) || d.date })));
    } catch {} finally { setLoading(false); }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <p style={{ color: C.black, fontWeight: 700, fontSize: 12, margin: '0 0 6px' }}>{label}</p>
        {payload.map(p => (
          <p key={p.name} style={{ color: p.color, fontSize: 12, margin: '2px 0', fontWeight: 600 }}>
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div style={s.card}>
      <div style={s.header}>
        <h3 style={s.title}>📈 Verification Trends</h3>
        <div style={s.tabs}>
          {['daily', 'weekly', 'monthly'].map(p => (
            <button key={p}
              style={{ ...s.tab, ...(period === p ? s.tabActive : {}) }}
              onClick={() => setPeriod(p)}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={s.placeholder}>
          <div style={s.spinner} />
          <p style={s.placeholderText}>Loading chart data...</p>
        </div>
      ) : data.length === 0 ? (
        <div style={s.placeholder}>
          <p style={{ fontSize: 32, margin: '0 0 8px' }}>📊</p>
          <p style={s.placeholderText}>No data yet. Start verifying emails to see trends!</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: C.gray }} />
            <YAxis tick={{ fontSize: 11, fill: C.gray }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Line type="monotone" dataKey="valid"   stroke={C.green} strokeWidth={2.5} dot={false} name="Valid"   activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="invalid" stroke={C.red}   strokeWidth={2.5} dot={false} name="Invalid" activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="risky"   stroke={C.amber} strokeWidth={2}   dot={false} name="Risky"   activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="total"   stroke={C.black} strokeWidth={2}   dot={false} name="Total"   strokeDasharray="5 5" activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const s = {
  card:            { background: '#ffffff', borderRadius: 14, border: '1.5px solid #e2e8f0', padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  header:          { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title:           { color: '#111111', fontSize: 15, fontWeight: 700, margin: 0 },
  tabs:            { display: 'flex', gap: 4 },
  tab:             { padding: '5px 13px', borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#f8fdf8', color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  tabActive:       { background: '#3cb74e', color: '#ffffff', border: '1.5px solid #3cb74e', fontWeight: 700 },
  placeholder:     { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 8 },
  placeholderText: { color: '#64748b', fontSize: 13, textAlign: 'center' },
  spinner:         { width: 32, height: 32, border: '3px solid #e2e8f0', borderTop: '3px solid #3cb74e', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
};
